import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSearch, FaFileDownload, FaEye } from "react-icons/fa";

const AdvertiserPaymentHistory = () => {
  // Pagination state for payments list
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 50;
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchPaymentHistory();
  }, []);

  const fetchPaymentHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/payments/history`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setPayments(response.data || []);
    } catch (error) {
      console.error("Error fetching payment history:", error);
      toast.error("Failed to load payment history");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (payment) => {
    setSelectedPayment(payment);
    setShowModal(true);
  };

  const filteredPayments = payments.filter((payment) => {
    const searchLower = searchTerm.toLowerCase();
    const paymentIdMatch = payment.paymentId?.toLowerCase().includes(searchLower) ||
      payment._id?.toLowerCase().includes(searchLower);

    const dateMatch = payment.date && new Date(payment.date).toLocaleDateString().toLowerCase().includes(searchLower);
    const amountMatch = payment.amount?.toString().includes(searchTerm);
    const methodMatch = payment.method?.toLowerCase().includes(searchLower);
    const statusMatch = payment.status?.toLowerCase().includes(searchLower);

    return paymentIdMatch || dateMatch || amountMatch || methodMatch || statusMatch;
  });

  const totalPages = Math.ceil(filteredPayments.length / rowsPerPage);
  const paginatedPayments = filteredPayments.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  return (
    <div className="space-y-6 pb-20 sm:pb-8">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header Section */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-xl sm:text-2xl font-black text-gray-900 tracking-tight">Financial Records</h1>
          <p className="text-sm text-gray-500 font-medium">Detailed history of all your advertising debits</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              placeholder="Search ID, date, or amount..."
              className="w-full pl-10 pr-4 py-3 bg-white border border-gray-100 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-2xl text-sm font-bold hover:bg-gray-800 transition-all active:scale-95 shadow-lg shadow-gray-200">
            <FaFileDownload />
            <span>Export CSV</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-20 flex flex-col items-center justify-center text-gray-400">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-sm font-bold">Safely loading records...</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Desktop Table - Hidden on Mobile */}
          <div className="hidden md:block bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr>
                  {["Payment ID", "Date", "Method", "Amount", "GST", "Status", "Action"].map((h) => (
                    <th key={h} className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedPayments.map((p) => (
                  <tr key={p.id || p._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-sm font-bold text-gray-900 font-mono">{p.paymentId || "N/A"}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-medium">{new Date(p.date).toLocaleDateString()}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 capitalize">{p.method}</td>
                    <td className="px-6 py-4 text-sm font-black text-blue-600">₹{p.amount.toFixed(2)}</td>
                    <td className="px-6 py-4 text-sm text-gray-400">{p.gst ? `₹${p.gst.toFixed(2)}` : "-"}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider border ${p.status === "captured" ? "bg-emerald-50 text-emerald-600 border-emerald-100" :
                          p.status === "pending" ? "bg-amber-50 text-amber-600 border-amber-100" :
                            "bg-red-50 text-red-600 border-red-100"
                        }`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button onClick={() => handleViewDetails(p)} className="p-2 bg-gray-50 rounded-xl text-blue-600 hover:bg-blue-50 transition-colors">
                        <FaEye />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card List - Visible ONLY on Small Screens */}
          <div className="grid grid-cols-1 gap-3 md:hidden">
            {paginatedPayments.map((p) => (
              <div
                key={p.id || p._id}
                onClick={() => handleViewDetails(p)}
                className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm active:scale-[0.98] transition-all"
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">ID: {p.paymentId || "N/A"}</p>
                    <p className="text-xs text-gray-500 font-medium">{new Date(p.date).toLocaleDateString()} • {p.method}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${p.status === "captured" ? "text-emerald-600 bg-emerald-50" : "text-amber-600 bg-amber-50"
                    }`}>
                    {p.status}
                  </span>
                </div>
                <div className="flex justify-between items-end">
                  <div>
                    <p className="text-xs text-gray-400 font-medium">Transaction Amount</p>
                    <p className="text-xl font-black text-gray-900">₹{p.amount.toFixed(2)}</p>
                  </div>
                  <div className="p-2 bg-blue-50 rounded-xl text-blue-600">
                    <FaEye className="w-4 h-4" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {paginatedPayments.length === 0 && (
            <div className="py-20 text-center bg-white rounded-3xl border border-dashed border-gray-200">
              <p className="text-gray-400 font-bold">No records match your search criteria</p>
            </div>
          )}

          {/* Custom Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-4">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                Showing page {currentPage} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-xs font-bold text-gray-600 disabled:opacity-50 active:scale-95 transition-all"
                >
                  Prev
                </button>
                <div className="flex gap-1 overflow-x-auto max-w-[150px] no-scrollbar">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                    <button
                      key={n}
                      onClick={() => setCurrentPage(n)}
                      className={`min-w-[32px] h-8 rounded-lg text-xs font-bold transition-all ${currentPage === n ? 'bg-blue-600 text-white shadow-md shadow-blue-200' : 'bg-white text-gray-500'
                        }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-xs font-bold text-gray-600 disabled:opacity-50 active:scale-95 transition-all"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modern Modal */}
      {showModal && selectedPayment && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-sm w-full overflow-hidden animate-in fade-in zoom-in duration-300">
            <div className="relative p-8 pt-10">
              <button
                onClick={() => setShowModal(false)}
                className="absolute top-6 right-6 w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 hover:text-gray-900 transition-colors"
              >
                &times;
              </button>

              <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 mb-6 mx-auto">
                <FaFileDownload className="w-8 h-8" />
              </div>

              <h3 className="text-xl font-black text-center text-gray-900 mb-8">Transaction Details</h3>

              <div className="space-y-4">
                {[
                  { label: "Payment ID", val: selectedPayment.paymentId, mono: true },
                  { label: "Date", val: new Date(selectedPayment.date).toLocaleDateString() },
                  { label: "Method", val: selectedPayment.method },
                  { label: "Base Amount", val: `₹${selectedPayment.amount.toFixed(2)}` },
                  { label: "GST (18%)", val: `₹${(selectedPayment.gst || 0).toFixed(2)}` },
                ].map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center text-sm">
                    <span className="font-bold text-gray-400 uppercase text-[10px] tracking-widest">{item.label}</span>
                    <span className={`font-bold text-gray-700 ${item.mono ? 'font-mono' : ''}`}>{item.val}</span>
                  </div>
                ))}

                <div className="pt-4 mt-4 border-t border-gray-100 flex justify-between items-center">
                  <span className="font-black text-gray-900 uppercase text-xs tracking-widest">Total Paid</span>
                  <span className="text-2xl font-black text-blue-600">₹{(selectedPayment.totalCost || 0).toFixed(2)}</span>
                </div>
              </div>

              <button
                onClick={() => setShowModal(false)}
                className="w-full mt-8 py-4 bg-gray-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-gray-800 transition-all active:scale-95"
              >
                Close Receipt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvertiserPaymentHistory;
